import React, { useCallback, useEffect, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import { getDealerAdminInvoices, formatUsd, INVOICE_KIND_LABELS } from "../../../services/dealerGarage";
import BillingPanel, { InvoiceStatusChip } from "./BillingPanel";
import DepositPanel from "./DepositPanel";
import useCustomerSearch from "./useCustomerSearch";
import { formatDate, invoiceRef, customerLabel, customerRefOf, vehicleLabel, isOverdue } from "./dealerAdmin";
import CustomerLink from "./CustomerLink";

/**
 * The money view: every invoice still waiting on payment (overdue first), and a
 * customer lookup that opens their deposit balance and all their invoices.
 *
 * Props: onOpenPurchase(id), onOpenCustomer(ref)
 */
const BillingSection = ({ onOpenPurchase, onOpenCustomer }) => {
  const [unpaid, setUnpaid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [showList, setShowList] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [ledgerVersion, setLedgerVersion] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    getDealerAdminInvoices({ unpaid: "true", limit: 500 })
      .then((res) => {
        const rows = res?.message === "Success" ? res.data : [];
        // Overdue first, then by due date.
        rows.sort((a, b) => (isOverdue(b) - isOverdue(a)) || String(a.dueDate || "9").localeCompare(String(b.dueDate || "9")));
        setUnpaid(rows);
      })
      .catch(() => setUnpaid([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // A payment on a deposit invoice adds to the deposit balance, and an applied
  // deposit takes from it, so the ledger beside the invoices reloads with them.
  const invoicesChanged = useCallback(() => {
    load();
    setLedgerVersion((v) => v + 1);
  }, [load]);

  const matches = useCustomerSearch(userQuery, showList);

  const outstanding = unpaid.reduce((sum, i) => sum + Math.max(0, (i.total || 0) - (i.amountPaid || 0)), 0);

  const pick = (u) => {
    setCustomer(u);
    setUserQuery(`${u.firstName} ${u.lastName} (${u.email})`);
    setShowList(false);
  };

  return (
    <div>
      <h3 className={classes.headingH3}>Waiting on payment</h3>
      <p className={styles.hint}>{unpaid.length} invoice{unpaid.length === 1 ? "" : "s"} · {formatUsd(outstanding)} outstanding</p>
      <div className={`${classes.tableContainer} ${styles.scrollTable}`}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.tableHeaderRow}>
              <th className={classes.tableHead}>Invoice</th>
              <th className={classes.tableHead}>Customer</th>
              <th className={classes.tableHead}>For</th>
              <th className={classes.tableHead}>Due</th>
              <th className={classes.tableHead}>Balance</th>
              <th className={classes.tableHead}></th>
            </tr>
          </thead>
          <tbody>
            {loading && unpaid.length === 0 && (
              <tr className={classes.tableRow}><td className={classes.tableData} colSpan={6}>Loading...</td></tr>
            )}
            {!loading && unpaid.length === 0 && (
              <tr className={classes.tableRow}><td className={classes.tableData} colSpan={6}>Nothing outstanding.</td></tr>
            )}
            {unpaid.map((invoice) => (
              <tr key={invoice._id} className={classes.tableRow}>
                <td className={classes.tableData}>{invoiceRef(invoice)} <InvoiceStatusChip invoice={invoice} /></td>
                <td className={classes.tableData}>
                  <CustomerLink customer={customerRefOf(invoice)} onOpen={onOpenCustomer}>{customerLabel(invoice)}</CustomerLink>
                </td>
                <td className={classes.tableData}>
                  {INVOICE_KIND_LABELS[invoice.kind] || invoice.kind}
                  {invoice.vehicle ? <><br /><small>{vehicleLabel(invoice)}</small></> : null}
                </td>
                <td className={classes.tableData}>{formatDate(invoice.dueDate)}</td>
                <td className={classes.tableData}>{formatUsd(Math.max(0, invoice.total - invoice.amountPaid))}</td>
                <td className={classes.tableData}>
                  {invoice.purchaseId ? (
                    <button type="button" className={classes.tableButton} onClick={() => onOpenPurchase(invoice.purchaseId)}>Open purchase</button>
                  ) : (
                    <button
                      type="button"
                      className={classes.tableButton}
                      onClick={() => {
                        // The list comes with the customer filled in.
                        const u = invoice.userId;
                        if (u && typeof u === "object") pick(u);
                      }}
                    >
                      Open customer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className={classes.headingH3} style={{ marginTop: 24 }}>Customer deposits & invoices</h3>
      <div className={classes.formGroup}>
        <div className={classes.wfull}>
          <input
            className={classes.formInput}
            placeholder="Search a customer by name or email"
            value={userQuery}
            onChange={(e) => { setUserQuery(e.target.value); setShowList(true); }}
            onFocus={() => setShowList(true)}
            onBlur={() => setTimeout(() => setShowList(false), 150)}
          />
          {showList && matches.length > 0 && (
            <div className={classes.usersList}>
              {matches.map((u) => (
                <div key={u._id} className={classes.nameList} onMouseDown={() => pick(u)}>
                  {u.firstName} {u.lastName} <small>({u.email}){u.isGuest ? " · guest account" : ""}</small>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {customer && (
        <div className={`${styles.grid2} ${classes.stackedForm}`} style={{ alignItems: "start" }}>
          <div className={styles.panelSection}>
            <h3>Deposit</h3>
            <DepositPanel key={customer._id} userId={customer._id} refreshKey={ledgerVersion} />
          </div>
          <div className={styles.panelSection}>
            <h3>All invoices</h3>
            <BillingPanel key={customer._id} scope={{ userId: customer._id }} defaultKind="deposit" onChange={invoicesChanged} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSection;
